<template>
  <v-data-table
      dense
      :headers="headers"
      :items="items"
      :loading="loading"
      :options.sync="options"
      :server-items-length="total"
      :footer-props="{'items-per-page-options': [10, 20, 50, 100]}"
      :no-data-text="$t('login-history.empty')"
  >
    <template #item.date="{value}">
      {{ formatDate(value, $t('format_datetime')) }}
    </template>

    <template #item.success="{item}">
      <v-chip x-small label :color="item.success ? 'success' : 'error'" text-color="white">
        {{ $t(item.success ? 'login-history.success' : 'login-history.failure') }}
      </v-chip>
      <span v-if="!item.success && item.failureReason" class="ml-2 text-caption">
        {{ translated('login-history.reasons.', item.failureReason) }}
      </span>
    </template>

    <template #item.mechanism="{value}">
      {{ translated('login-history.mechanisms.', value) }}
    </template>

    <!-- Client : espace client ou application (plateforme et version quand l'application les envoie). -->
    <template #item.channel="{item}">
      {{ translated('login-history.channels.', item.channel) }}
      <span v-if="item.platform || item.appVersion" class="text-caption ml-1">
        ({{ [item.platform, item.appVersion].filter(v => v).join(' ') }})
      </span>
    </template>

    <template #item.ip="{value}">
      {{ value || '-' }}
    </template>

    <!-- Le navigateur complet est long : tronqué, entier au survol. -->
    <template #item.userAgent="{value}">
      <v-tooltip v-if="value" top max-width="500">
        <template #activator="{ on, attrs }">
          <span class="login-history-agent text-caption" v-bind="attrs" v-on="on">{{ value }}</span>
        </template>
        <span>{{ value }}</span>
      </v-tooltip>
      <span v-else>-</span>
    </template>
  </v-data-table>
</template>

<script>
/**
 * L'historique de connexion d'un compte (gestionnaire ou client) : chaque tentative, réussie ou refusée, avec la
 * méthode, l'adresse IP et le navigateur, les plus récentes d'abord. `load(page, limit)` rend la page demandée
 * ({total, items}) ; `withChannel` ajoute la colonne application / espace client (clients).
 */
export default {
  props: {
    load: {type: Function, required: true},
    withChannel: {type: Boolean, default: false},
  },

  data() {
    return {
      loading: false,
      options: {page: 1, itemsPerPage: 10},
      total: 0,
      items: [],
    }
  },

  computed: {
    headers() {
      return [
        {text: this.$t('login-history.date'), value: 'date', sortable: false, width: 160},
        {text: this.$t('login-history.result'), value: 'success', sortable: false},
        {text: this.$t('login-history.mechanism'), value: 'mechanism', sortable: false, width: 120},
        ...(this.withChannel ? [{text: this.$t('login-history.channel'), value: 'channel', sortable: false, width: 180}] : []),
        {text: this.$t('login-history.ip'), value: 'ip', sortable: false, width: 140},
        {text: this.$t('login-history.browser'), value: 'userAgent', sortable: false},
      ]
    },
  },

  watch: {
    options: {
      handler() {
        this.loadData()
      },
      deep: true,
    },
    load() {
      this.options = {...this.options, page: 1}
    },
  },

  methods: {
    loadData() {
      this.loading = true
      const limit = this.options.itemsPerPage > 0 ? this.options.itemsPerPage : 100
      this.load(this.options.page, limit).then(response => {
        this.total = response.total
        this.items = response.items
        this.loading = false
      }).catch(err => {
        this.notifyError(err)
        this.loading = false
      })
    },

    // Une valeur que l'écran ne connaît pas encore s'affiche telle quelle.
    translated(prefix, value) {
      if (!value) return '-'
      return this.$te(prefix + value) ? this.$t(prefix + value) : value
    },
  },
}
</script>

<style lang="scss">
.login-history-agent {
  display: inline-block;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}
</style>
